import {
  IsIn,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  Min,
} from 'class-validator';
import { FIELD_VALUE_SOURCES, type FieldValueSource } from '../../label/field-value-source';

/**
 * Not a Prisma enum: `IngredientAllergen.category` and `LotAllergenDto`
 * already validate the same two values inline. Named here so this file does
 * not repeat a bare string-literal union, without touching the existing
 * lot DTO (out of scope for this agent).
 */
export const ALLERGEN_CATEGORY_KINDS = ['CONTAINS', 'MAY_CONTAIN'] as const;
export type AllergenCategoryKind = (typeof ALLERGEN_CATEGORY_KINDS)[number];

/**
 * One allergen declaration on an Ingredient. `substanceId` is what
 * art. 21 §1 lett. b actually requires on a CONTAINS row (the substance, not
 * the category) — the confirmation gate blocks when it is missing.
 * `sourceDocumentId` points at the proof (supplier label photo or DDT).
 */
export class IngredientAllergenDto {
  @IsUUID()
  allergenId!: string;

  /**
   * Id opaco della riga di catalogo, non un uuid garantito: alcune basi dati
   * portano sostanze create prima del seed con id deterministici non conformi
   * (`27000000-0000-0000-0000-000000000001` = grano), e `@IsUUID()` le
   * rifiutava bloccando il salvataggio senza alcun messaggio a video.
   * L'esistenza della sostanza la garantisce la foreign key.
   */
  @IsOptional()
  @IsString()
  @IsNotEmpty()
  substanceId?: string;

  @IsIn(ALLERGEN_CATEGORY_KINDS)
  category!: AllergenCategoryKind;

  @IsIn(FIELD_VALUE_SOURCES)
  source!: FieldValueSource;

  @IsOptional()
  @IsUUID()
  sourceDocumentId?: string;

  /**
   * SO2 totale dell'ingrediente come acquistato, in mg/kg (Allegato II punto
   * 12 Reg. UE 1169/2011). Significativo solo su una riga CONTAINS della
   * sostanza `sulphites`. Assente = concentrazione non dichiarata: i solfiti
   * restano dichiarati (fail-closed, `shared/src/label/sulphite-threshold.ts`).
   */
  @IsOptional()
  @IsNumber({ maxDecimalPlaces: 3 })
  @Min(0)
  @Max(100000)
  so2TotalMgPerKg?: number;
}
